<template>
  <div class="page__limit">
    <transition name="fade">
      <div v-if="alert.visibility" class="page-form__error">
        <v-icon class="page-form__error__icon" color="error">warning</v-icon>
        <span class="page-form__error__text">{{ alert.message }}</span>
      </div>
    </transition>

    <div class="page-form__container">
      <div class="page-form__wrapper">
        <div class="page-form">
          <h2 class="page-form__title">{{ $t(`${namespace}.formTitle`) }}</h2>
          <p>{{ $t(`${namespace}.formSubtitle`) }}</p>

          <form class="form--custom" novalidate @submit.prevent="handleSubmit">
            <v-layout row wrap>
              <v-flex sm6 xs12>
                <div class="pr-2--desktop">
                  <DatePickerInput
                    label="Mês de referência"
                    placeholder="MM/AAAA"
                    name="mês de referência"
                    required
                    format="YYYY-MM"
                    :submit="submit"
                    :resetField="resetField"
                    @change="(val) => setValue('monthAndYear', val)"
                  />
                </div>
              </v-flex>

              <v-flex sm6 xs12>
                <div class="pl-2--desktop">
                  <DatePickerInput
                    label="Data da emissão"
                    placeholder="DD/MM/AAAA"
                    name="data da emissão"
                    required
                    :min="today"
                    :submit="submit"
                    :resetField="resetField"
                    openOnClick
                    @change="(val) => setValue('emissionDate', val)"
                  />
                </div>
              </v-flex>
            </v-layout>

            <div class="form__drag-in-drop" :class="{ 'form__drag-in-drop--error': !hasFile }">
              <input
                type="file"
                accept=".xlsx, .xls, .csv"
                ref="file"
                @change="uploadFile($event.target)"
              >
              <label v-show="!Boolean(file)">
                <span v-html="$t(`${namespace}.uploadText`)" class="form__drag-in-drop__text" />
                <v-btn large depressed round color="accent" :loading="isLoading" :disabled="isLoading">
                  <v-icon class="mr-1">cloud_upload</v-icon>
                  <span class="hidden-xs--inline">{{ $t(`${namespace}.uploadBtnLabel`) }}</span>
                  <span class="visible-xs--inline">{{ $t('Common.import') }}</span>
                </v-btn>
              </label>

              <label v-show="Boolean(file)">
                <span class="form__drag-in-drop__text">
                  <strong v-html="$t(`${namespace}.importNfeClick`)" />
                </span>
                <v-icon color="success" size="36">check_circle</v-icon>
              </label>

              <div v-if="!hasFile" class="form__drag-in-drop--message v-messages">
                {{ $t(`${namespace}.uploadMessageError`) }}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import DatePickerInput from './DatePickerInput'

export default {
  name: 'Form',
  props: {
    submit: {
      type: Boolean
    },
    status: {
      type: Number
    }
  },
  components: {
    DatePickerInput
  },
  computed: {
    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    },

    today () {
      return new Date().toJSON().slice(0, 10)
    }
  },
  data () {
    return {
      namespace: 'Views.ImportNfePage',
      requestId: null,
      monthAndYear: null,
      emissionDate: null,
      startDate: null,
      endDate: null,
      file: null,
      hasFile: true,
      alert: {
        visibility: false,
        message: null
      },
      resetField: false
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    resetFields () {
      this.monthAndYear = null
      this.emissionDate = null
      this.startDate = null
      this.endDate = null
      this.file = null
      this.hasFile = true
      this.alert = {
        visibility: false,
        message: null
      }
      this.resetField = !this.resetField
    },

    setValue (field, val) {
      this[field] = val
    },

    uploadFile (elem) {
      if (!elem.files.length) {
        this.hasFile = false
        return
      }

      console.log('file size', elem.files[0].size)
      this.hasFile = true
      this.file = elem.files[0]
      this.$refs.file.value = null
    },

    formatDate (str) {
      return str.split('/').reverse().join('-')
    },

    getExtendedMonth (str) {
      moment.locale('pt-br')
      const date = this.formatDate(`01/${str}`)
      const newStr = `${moment(date).format('MMMM')}/${moment(date).format('YYYY')}`
      return newStr
    },

    clearInterval () {
      clearInterval(this.requestId)
    },

    showAlertError (message) {
      this.alert = { visibility: true, message }
      setTimeout(() => { this.hideAlertError() }, 4000)
    },

    hideAlertError () {
      this.alert = { visibility: false, message: null }
    },

    async getCityHallInvoiceStatus (id) {
      try {
        const { data: res } = await this.$apiService.getCityHallInvoiceStatus(id)

        if (res.status === 'ERROR') {
          throw new Error()
        }

        if (res.status === 'SUCCESS') {
          this.resetFields()
          this.clearInterval()
          this.$emit('setCurrentStatus', STATUS_SUCCESS)
          this.showAlert({
            type: 'success',
            message: this.$t(`${this.namespace}.formSuccess`)
          })
        }
      } catch (err) {
        console.log('ERROR:', err)
        this.clearInterval()
        this.$emit('setCurrentStatus', STATUS_FAILED)
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.formError`)
        })
      }
    },

    async handleSubmit () {
      try {
        const isValid = await this.$validator.validateAll()

        if (
          isValid &&
          Boolean(this.monthAndYear) &&
          Boolean(this.emissionDate) &&
          Boolean(this.file)
        ) {
          const { emissionDate, endDate, file, monthAndYear, startDate } = this
          const formData = new FormData()

          this.$emit('setCurrentStatus', STATUS_IS_LOADING)

          // DEBUG
          console.log({
            runTest: true,
            monthAndYear: this.getExtendedMonth(monthAndYear),
            emissionDate: this.formatDate(emissionDate),
            startDate,
            endDate,
            file,
            textToDescription: ''
          })

          formData.append('runTest', process.env.NODE_ENV !== 'production') // only test
          formData.append('monthAndYear', this.getExtendedMonth(monthAndYear)) // format January/YYYY
          formData.append('emissionDate', this.formatDate(emissionDate)) // format YYYY-MM-DD
          formData.append('startDate', startDate) // format YYYY-MM-DD
          formData.append('endDate', endDate) // format YYYY-MM-DD
          formData.append('file', file)
          // formData.append('textToDescription', '')

          const { data: res } = await this.$apiService.sendCityHallInvoice(formData)
          const uploadId = res.id

          this.requestId = setInterval(() => {
            this.getCityHallInvoiceStatus(uploadId)
          }, 1000)
        } else {
          if (!this.file) this.hasFile = false
          this.clearInterval()
          this.showAlertError(this.$t(`${this.namespace}.formGenericError`))
        }
      } catch (err) {
        console.log('ERROR', err)
        this.$emit('setCurrentStatus', STATUS_FAILED)
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.formError`)
        })
      }
    }
  },
  watch: {
    submit () {
      this.handleSubmit()
    },

    monthAndYear (val) {
      if (val) {
        const startDate = this.formatDate(`01/${val}`)
        const endDate = moment(startDate).endOf('month').format('YYYY-MM-DD')
        console.log({ startDate, endDate })
        this.startDate = startDate
        this.endDate = endDate
      }
    }
  },
  beforeDestroy () {
    this.clearInterval()
  }
}
</script>

<style lang="scss" scoped>
.page__limit {
  position: relative;
}
</style>
